/**
 * actions.js — el brazo ejecutor del modo automático del tutorial.
 *
 * Los pasos de un guion pueden declarar `auto: [ ...acciones ]`. Aquí se
 * traducen a interacciones reales con la pantalla (escribir en un campo, pulsar
 * un botón, cambiar de pestaña) o a llamadas a la API cuando el gesto real es
 * poco fiable de automatizar (arrastrar en el lienzo del Diseñador, buscar un CI
 * en un selector con miles de opciones...).
 *
 * Dos reglas de convivencia:
 *   · Escribir en un campo de React no basta con `el.value = x`: React escucha
 *     el evento, así que se usa el setter nativo del prototipo y se dispara
 *     `input`/`change` (mismo truco que usan las herramientas de test).
 *   · Cada clic deja un pulso visual (`onPulse`) para que se vea dónde se ha
 *     pulsado. Sin eso, el usuario ve la pantalla cambiar "sola" y no aprende
 *     el gesto.
 */
import api from '../services/api';
import { findTarget, findMenuItemLink, findSidebarToggles, isVisible } from './dom';

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** Escribe en un input/textarea de React como lo haría una persona. */
export function setReactValue(el, value) {
  const proto = el.tagName === 'TEXTAREA' ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
  if (setter) setter.call(el, value);
  else el.value = value;
  el.dispatchEvent(new Event('input', { bubbles: true }));
  el.dispatchEvent(new Event('change', { bubbles: true }));
}

/** Cambia un `<select>` de React. */
export function setReactSelect(el, value) {
  el.value = value;
  el.dispatchEvent(new Event('change', { bubbles: true }));
}

/** Espera a que una condición se cumpla, con tope de tiempo. */
async function waitUntil(predicate, timeout = 6000, every = 200) {
  const limit = Date.now() + timeout;
  while (Date.now() < limit) {
    try { if (predicate()) return true; } catch { /* aún no */ }
    await sleep(every);
  }
  return false;
}

/** Igual, pero devolviendo el elemento (para luego pulsarlo). */
async function waitForElement(fn, timeout = 8000, every = 200) {
  const limit = Date.now() + timeout;
  while (Date.now() < limit) {
    try { const el = fn(); if (el) return el; } catch { /* aún no */ }
    await sleep(every);
  }
  return null;
}

/**
 * Busca un nodo del lienzo por su nombre.
 * Los textos del grafo van TRUNCADOS («VPN corporativa (…»), así que no vale
 * comparar entero: se puntúa por prefijo común y se exige un mínimo para no
 * confundir «RADIUS Corporativo» con «RADIUS Auth».
 */
function findCanvasNode(needle) {
  const norm = (s) => String(s).toLowerCase().replace(/[^a-z0-9áéíóúñ ]/gi, '').replace(/\s+/g, ' ').trim();
  const want = norm(needle);
  if (!want) return null;
  const candidates = [...document.querySelectorAll('.sd-canvas svg text, .sd-canvas svg title')];
  let best = null;
  let bestScore = 0;
  for (const el of candidates) {
    const txt = norm(el.textContent || '');
    if (!txt) continue;
    let i = 0;
    while (i < Math.min(txt.length, want.length) && txt[i] === want[i]) i++;
    if (i > bestScore) { bestScore = i; best = el; }
  }
  if (!best || bestScore < 7) return null;
  let g = best;
  while (g && g.tagName && g.tagName.toLowerCase() !== 'g') g = g.parentElement;
  return g || null;
}

function clickElement(el, onPulse, onMove) {
  if (!el) throw new Error('elemento no encontrado');
  if (typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  const r = el.getBoundingClientRect();
  // El cursor viaja hasta el elemento ANTES del clic: así se ve a dónde va y
  // qué va a pulsar, en vez de aparecer el efecto de golpe.
  onMove?.({ x: r.left + r.width / 2, y: r.top + r.height / 2 });
  onPulse?.({ x: r.left + r.width / 2, y: r.top + r.height / 2 });
  // Los nodos del lienzo son `<g>` de SVG, y un SVGElement NO tiene `.click()`
  // (solo HTMLElement). Con el evento nativo React los recibe igual.
  if (typeof el.click === 'function') el.click();
  else el.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
}

/**
 * Navegar como lo hace una persona: busca la entrada en el menú lateral, abre el
 * grupo si está cerrado, lleva el cursor hasta ella y la pulsa.
 * Si no hay entrada de menú (o no está visible), se cae a la navegación directa.
 */
export async function goViaMenu(path, { onMove, onPulse, speed = 1, navigate, say } = {}) {
  const clean = String(path).split('?')[0];
  const pace = (ms) => sleep(Math.max(120, ms / speed));

  let link = findMenuItemLink(clean);
  if (!link || !isVisible(link)) {
    // El grupo puede estar plegado: se abre buscando el que contiene el enlace.
    const toggles = findSidebarToggles().filter(t => !t.abierto);
    for (const t of toggles) {
      const before = findMenuItemLink(clean);
      clickElement(t.el, onPulse, onMove);
      await pace(700);
      const after = findMenuItemLink(clean);
      if (after && isVisible(after)) { link = after; say?.(`Menú «${t.text}» abierto`); break; }
      if (after !== before && after && isVisible(after)) { link = after; break; }
    }
    link = link && isVisible(link) ? link : findMenuItemLink(clean);
  }

  if (!link || !isVisible(link) || typeof link.click !== 'function') {
    // Sin entrada visible (menú colapsado, item oculto por preferencias...):
    // se navega directo para no dejar el tutorial a medias.
    navigate?.(path);
    await pace(900);
    return false;
  }

  clickElement(link, onPulse, onMove);
  await pace(1000);
  return true;
}

/**
 * Ejecuta una acción. Devuelve el nombre de la acción si algo falla, para que el
 * motor pueda parar y decirlo en el panel.
 */
async function runOne(action, { companyId, serviceId, onPulse, onMove, speed, say, onServiceId }) {
  const pace = (ms) => sleep(Math.max(120, ms / speed));

  // ── Navegador ──
  if (action.click) {
    const el = findTarget(action.click);
    await pace(300);
    clickElement(el, onPulse, onMove);
    await pace(action.after || 500);
    return null;
  }

  if (action.clickAll) {
    for (const sel of action.clickAll) {
      clickElement(findTarget(sel), onPulse, onMove);
      await pace(500);
    }
    return null;
  }

  if (action.fill) {
    const [sel, value] = action.fill;
    const el = document.querySelector(sel);
    if (!el) throw new Error(`campo ${sel} no encontrado`);
    el.focus();
    await pace(250);
    setReactValue(el, value);
    await pace(400);
    return null;
  }

  if (action.select) {
    const [sel, value] = action.select;
    const el = document.querySelector(sel);
    if (!el) throw new Error(`desplegable ${sel} no encontrado`);
    setReactSelect(el, value);
    await pace(600);
    return null;
  }

  if (action.tab) {
    const el = findTarget(`@text=${action.tab}`);
    await pace(200);
    clickElement(el, onPulse, onMove);
    await pace(700);
    return null;
  }

  if (action.wait) { await pace(action.wait); return null; }

  if (action.waitFor) {
    const ok = await waitUntil(() => Boolean(findTarget(action.waitFor)), 12000);
    // `optional` es para esperas de cortesía (que el lienzo haya montado, por
    // ejemplo): si no llega, la demo sigue en vez de quedarse parada.
    if (!ok && !action.optional) throw new Error(`no aparece ${action.waitFor}`);
    await pace(400);
    return null;
  }

  // Guardar el servicio: el botón está deshabilitado hasta que el formulario
  // cambia, así que se reintenta un par de veces antes de darse por vencido.
  if (action.save) {
    const sel = '.dv-topbar-actions .dv-btn-primary';
    const ready = await waitUntil(() => {
      const b = document.querySelector(sel);
      return b && !b.disabled;
    }, 6000);
    if (!ready) throw new Error('el botón Guardar sigue deshabilitado');
    clickElement(document.querySelector(sel), onPulse, onMove);
    await pace(action.wait || 1800);

    // Se localiza el servicio recién creado y se avisa al motor en el acto: el
    // resto del guion (accesos, relaciones, volver a su ficha) necesita su id, y
    // esperar a que lo deduzca un efecto posterior lo dejaba a null.
    if (action.trackName) {
      const rowFor = () => {
        const needle = String(action.trackName).toLowerCase();
        const matches = [...document.querySelectorAll('tr[id^="svc-row-"]')]
          .filter(r => (r.textContent || '').toLowerCase().includes(needle));
        return matches.sort((a, b) =>
          Number(b.id.replace('svc-row-', '')) - Number(a.id.replace('svc-row-', '')))[0];
      };
      await waitUntil(() => Boolean(rowFor()), 8000);
      const row = rowFor();
      const newId = row ? Number(row.id.replace('svc-row-', '')) : null;
      if (newId) onServiceId?.(newId, action.trackName);
      else throw new Error('no encuentro el servicio recién creado en el catálogo');
    }
    return null;
  }

  // Abrir la ficha de un servicio del catálogo, buscándolo por su nombre.
  if (action.openService) {
    const needle = String(action.openService).toLowerCase();
    const rowFor = () => {
      const matches = [...document.querySelectorAll('tr[id^="svc-row-"]')]
        .filter(r => (r.textContent || '').toLowerCase().includes(needle));
      return matches.sort((a, b) =>
        Number(b.id.replace('svc-row-', '')) - Number(a.id.replace('svc-row-', '')))[0];
    };
    // Puede que el catálogo acabe de montarse: se espera en vez de fallar.
    await waitUntil(() => Boolean(rowFor()), 8000);
    const row = rowFor();
    if (!row) throw new Error(`no veo el servicio ${action.openService} en el catálogo`);
    clickElement(row, onPulse, onMove);
    const ok = await waitUntil(() => Boolean(document.querySelector('.dv-overlay')), 6000);
    if (!ok) throw new Error('no se ha abierto la ficha');
    await pace(900);
    return null;
  }

  // ── Lienzo del Diseñador: el gesto real, con clics visibles ──
  if (action.canvasAdd) {
    // Si el catálogo tiene buscador, se filtra primero: la lista es larga y así
    // la fila buscada está a la vista (y se ve el gesto de buscar).
    const search = document.querySelector('input[placeholder^="Buscar módulo"], .sd-catalog input[type="text"], .sd-sidebar input[type="text"]');
    if (search) {
      search.focus();
      setReactValue(search, action.canvasAdd);
      await pace(900);
    }
    const rowFor = () => [...document.querySelectorAll('.sd-result-row')]
      .find(r => (r.textContent || '').toLowerCase().includes(String(action.canvasAdd).toLowerCase()));
    const row = await waitForElement(rowFor, 8000);
    if (!row) {
      // Puede que el elemento ya esté en el lienzo y por eso el catálogo no lo
      // ofrezca (según cómo filtre el diseño). Si el nodo está, el paso vale.
      if (findCanvasNode(action.canvasAdd)) { await pace(600); return null; }
      throw new Error(`no veo «${action.canvasAdd}» en el catálogo del diseñador`);
    }
    const btn = row.querySelector('.sd-add-btn');
    if (!btn) {
      // Sin botón ＋: normalmente porque ya está en el mapa. No es un error.
      if (findCanvasNode(action.canvasAdd)) { await pace(600); return null; }
      throw new Error(`la fila de «${action.canvasAdd}» no tiene botón de añadir`);
    }
    clickElement(btn, onPulse, onMove);
    await pace(1400);              // el lienzo recoloca los nodos
    return null;
  }

  if (action.canvasClick) {
    const node = await waitForElement(() => findCanvasNode(action.canvasClick), 8000);
    if (!node) throw new Error(`no encuentro el nodo «${action.canvasClick}» en el lienzo`);
    clickElement(node, onPulse, onMove);
    await pace(action.wait || 900);
    return null;
  }

  if (action.relationModal) {
    const modal = await waitForElement(() => document.querySelector('.sd-modal'), 8000);
    if (!modal) throw new Error('no se ha abierto la ventana de la relación');
    const sel = modal.querySelector('select');
    if (!sel) throw new Error('la ventana de relación no tiene selector de tipo');

    // El `<option>` puede llevar como valor el id del tipo y el nombre entre
    // paréntesis en el texto («se autentica en (authenticates_on)»), así que se
    // busca la opción por su texto y se usa SU valor.
    const wanted = String(action.relationModal);
    const opt = [...sel.options].find(o => (o.textContent || '').includes(`(${wanted})`))
      || [...sel.options].find(o => o.value === wanted)
      || [...sel.options].find(o => (o.textContent || '').toLowerCase().includes(wanted.replace(/_/g, ' ')));
    if (!opt) throw new Error(`el catálogo de relaciones no tiene «${wanted}»`);
    setReactSelect(sel, opt.value);
    await pace(700);

    const save = [...modal.querySelectorAll('button')]
      .find(b => /guardar relación/i.test(b.textContent || ''));
    if (!save) throw new Error('la ventana de relación no tiene botón de guardar');
    // El botón se habilita en cuanto React registra el tipo elegido.
    const ready = await waitUntil(() => {
      const b = [...document.querySelectorAll('.sd-modal button')]
        .find(x => /guardar relación/i.test(x.textContent || ''));
      return b && !b.disabled;
    }, 4000);
    if (!ready) throw new Error(`no he podido seleccionar el tipo «${wanted}»`);
    clickElement(save, onPulse, onMove);
    await pace(action.wait || 2200);   // guarda y recarga el grafo: aparece la flecha
    return null;
  }

  // ── Por API (gestos que no merece la pena automatizar) ──
  if (action.addAccess) {
    if (!serviceId) throw new Error('todavía no hay servicio');
    await api.createServiceAccessPoint(serviceId, action.addAccess, companyId);
    say?.('Acceso creado');
    await pace(900);
    return null;
  }

  if (action.linkService) {
    if (!serviceId) throw new Error('todavía no hay servicio');
    const { targetServiceName, targetServiceType, relation = 'depends_on', direction = 'out' } = action.linkService;
    const list = await api.getServices(companyId);
    const services = Array.isArray(list) ? list : (list.services || []);
    const target = services.find(s =>
      (targetServiceName && (s.display_name === targetServiceName || s.name === targetServiceName)) ||
      (targetServiceType && s.service_type === targetServiceType),
    );
    if (!target) throw new Error(`no encuentro el servicio ${targetServiceName || targetServiceType}`);
    const typesRes = await api.sdListRelationTypes(companyId);
    const types = typesRes?.data || [];
    const tipo = types.find(t => t.name === relation);
    if (!tipo) throw new Error(`el catálogo no tiene la relación ${relation}`);
    const source = direction === 'out' ? serviceId : target.id;
    const dest = direction === 'out' ? target.id : serviceId;
    await api.sdConnect(serviceId, companyId, {
      relation_type_id: tipo.id,
      source_ci_type: 'service',
      source_ci_id: source,
      target_ci_type: 'service',
      target_ci_id: dest,
    });
    say?.(`Enlazado con ${target.display_name || target.name}`);
    await pace(900);
    return null;
  }

  throw new Error(`acción desconocida: ${Object.keys(action)[0]}`);
}

/**
 * Ejecuta las acciones de un paso en orden. Devuelve `{ ok, error }`: si algo
 * falla, el motor para la reproducción y avisa, en vez de seguir haciendo cosas
 * a medias sobre datos reales.
 */
export async function performStep(step, opts) {
  const actions = step.auto || [];
  for (const action of actions) {
    try {
      await runOne(action, opts);
    } catch (e) {
      // Las acciones marcadas como opcionales no hunden el paso: sirven para
      // «trae esto si no está ya».
      if (action.optional) continue;
      return { ok: false, error: e.message, action: Object.keys(action)[0] };
    }
  }
  return { ok: true };
}

export { sleep };
